import cn from 'classnames';
import compact from 'lodash/compact';
import find from 'lodash/find';
import isNil from 'lodash/isNil';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';

import EPCheckbox from '@/comp/EPCheckbox';
import EPTable from '@/comp/EPTable';
import { EPSelect, EPSelectOption } from '@/components';
import { compressorTypes } from '@/constants/deviceTypeEnum';
import { useStores } from '@/hooks/use-stores';
import { Setpoint } from '@/store/Engine/common';
import {
  EngineStepsEnum,
  EngineSubstepsEnum,
  SetpointsConfiguration as SetpointsConfigurationConstant,
} from '@/store/Engine/constants';
import { FallbackRule } from '@/store/Engine/FallbackScenarios';
import { SensorPriority } from '@/store/Engine/SensorsConfiguration';
import { SetpointsConfiguration } from '@/store/Engine/SetpointsConfiguration';
import TableHeaderCell from '@/comp/pages/EnginePage/Engine/Common/TableHeaderCell/TableHeaderCell';
import ValidationTooltip from '@/comp/pages/EnginePage/Engine/Common/ValidationTooltip/ValidationTooltip';
import { maxLength, onlyDigits, onlyDigitsAndDash } from '@/utils/util-functions';

import { CheckboxContainer, CompressorNameContainer, DefaultRuleContainer, Input } from './styled';
import {
  bandConfigurationOptions,
  columnsMapping,
  compressorTypesAbbreviations,
  DefaultSetpointsFrom,
  LONG_DASH,
  ValidMetricTypes,
} from '../../EngineVersionUtils';
import { intersection } from 'lodash';
import { useMeasurement } from '@/hooks/useMeasurement';
import { sortSetpointsByElementIndex, validateNewFallbackScenarioSetpoints } from '@/store/Engine/utils';
import MetricTooltipContent from '../../Common/Range/MetricTooltipContent';
import { EngineStore } from '@/store/Engine/EngineStore';
import DevicesStore from '@/store/Devices/DevicesStore';
import StationStore from '@/store/Station/StationStore';

const inputCountArgs = maxLength(4);

const getFieldEditableStatusForAllCompressors = (compressors, compressorId, field) => {
  const compressor = find(compressors, { id: compressorId });
  const editableFields = compressor?.editableFields;

  return !!intersection(editableFields, columnsMapping[field])?.length;
};

const DefaultSetpointsComponent = observer(
  ({
    setpoints,
    isEditMode,
    className,
    from,
    rulesObject,
    disabledCompressors,
    hasTriedToSubmit,
    type,
    isNewFallbackScenario,
  }: {
    isEditMode: boolean;
    setpoints?: Setpoint[];
    className?: string;
    from?: DefaultSetpointsFrom;
    rulesObject?: SensorPriority | FallbackRule;
    disabledCompressors?: string;
    hasTriedToSubmit?: boolean;
    type?: string;
    isNewFallbackScenario?: boolean;
  }) => {
    const {
      engineStore,
      devicesStore,
      stationStore,
    }: { engineStore?: EngineStore; devicesStore?: DevicesStore; stationStore?: StationStore } = useStores();
    const { t } = useTranslation();
    const fromRulesEditor = from === 'rulesEditor';
    const fromRulesGeneratorModal = from === 'rulesGeneratorModal';
    const [editingSetpointId, setEditingSetpointId] = useState('');

    const validationMessages = engineStore.validationMessages;
    const editEnabled = isEditMode && !engineStore.isReadOnly;
    const canOpenCompressorMetricTooltip = engineStore.isReadOnly ? undefined : true;

    const [, setIsDropdownOpen] = useState(false);

    const compressors = useMemo(
      () => engineStore.applicableCompressorsForEngine,
      [engineStore.applicableCompressorsForEngine],
    );
    const engineMetricSetpoints = useMemo(() => engineStore.engineMetricSetpoints, [engineStore.engineMetricSetpoints]);

    const metricsByCompressor = useMemo(() => {
      const result = {};
      Object.values(engineMetricSetpoints || {}).forEach((metric: { metricType: string; deviceId: string }) => {
        if (Object.values(ValidMetricTypes).includes(metric.metricType as ValidMetricTypes)) {
          if (!result[metric.deviceId]) {
            result[metric.deviceId] = [];
          }
          result[metric.deviceId].push(metric);
        }
      });
      return result;
    }, [engineMetricSetpoints]);

    const compressorTooltipContents = useMemo(() => {
      const tooltips = {};
      Object.keys(metricsByCompressor).forEach(compressorId => {
        const metrics = metricsByCompressor[compressorId];
        tooltips[compressorId] =
          metrics && metrics.length > 0 ? <MetricTooltipContent metrics={metrics} /> : t('noMetricsAvailable');
      });
      return tooltips;
    }, [metricsByCompressor, t]);

    const sortedCompressors = useMemo(() => devicesStore?.sortedCompressors, [devicesStore.sortedCompressors]);

    const { tvalue } = useMeasurement();

    const getPopupContainer = trigger => trigger.parentElement;

    useEffect(() => {
      if (!fromRulesGeneratorModal && !engineStore.defaultSetpointsConfiguration?.setpoints?.length) {
        engineStore.defaultSetpointsConfiguration = new SetpointsConfiguration(
          null,
          engineStore.compressorGroups,
          stationStore.currentStation?.devices?.filter(device => compressorTypes.includes(device.deviceType)),
          compressors,
          tvalue,
          engineStore.unitMesurementSystem,
          false,
          sortedCompressors,
        );
      }
    }, []);

    useEffect(() => {
      if (hasTriedToSubmit) setEditingSetpointId(null);
    }, [hasTriedToSubmit]);

    const onDefaultSetpointFieldChange = (key, field) => e => {
      setEditingSetpointId(`${key}-${field}`);
      const changedValue =
        field === 'modulate' ? e?.target?.checked : field === 'bandConfiguration' ? e : e?.target?.value;
      fromRulesGeneratorModal
        ? rulesObject?.setField('rules', changedValue, field, key)
        : engineStore[
            fromRulesEditor ? SetpointsConfigurationConstant : EngineStepsEnum.DefaultSetpointsConfiguration
          ]?.setSetpointField(key, field, changedValue);

      if (fromRulesGeneratorModal && isNewFallbackScenario) {
        engineStore.validationMessages = validateNewFallbackScenarioSetpoints(
          rulesObject.rules,
          disabledCompressors,
          devicesStore?.devicesMetadata,
        );
      } else if (hasTriedToSubmit) {
        engineStore.validate(
          type ? EngineStepsEnum.SensorsConfiguration : EngineSubstepsEnum.FallbackScenarios,
          type ? `${type}SensorsPriority` : null,
          null,
          stationStore.currentStation.devices,
        );
      }
    };

    const onDefaultSetpointKeyPress = event => onlyDigits(event);
    const onOrderColumnKeyPress = event => onlyDigitsAndDash(event);

    const onInputScroll = e => e?.target?.blur();

    const onInputBlur = () => setEditingSetpointId(null);

    const columns = [
      {
        dataIndex: 'compressorNumber',
        key: 'compressorNumber',
        title: <TableHeaderCell titleKey="No" tooltipContent={t('compressorNumber')} />,
        width: '5%',

        render: (compressorNumber, { compressorId }) => (
          <span className={cn('label-text', disabledCompressors?.includes(compressorId) && 'disabled-compressor')}>
            {compressorNumber}
          </span>
        ),
        align: 'center' as const,
      },
      {
        dataIndex: 'compressorId',
        key: 'compressorId',
        title: <TableHeaderCell titleKey="Compressor" className="compressor-header" />,
        width: '50%',
        render: (compressorId, { compressorName }) => {
          const compressor = find(compressors, { id: compressorId });
          return (
            <CompressorNameContainer $disabledCompressor={disabledCompressors?.includes(compressorId)}>
              {compact([
                compressor?.name,
                compressor?.deviceModel?.modelSpecs?.hp,
                compressorTypesAbbreviations[compressor?.deviceModel?.deviceTypes?.[0]],
              ]).join(' | ') || compressorName}
            </CompressorNameContainer>
          );
        },
      },
      {
        dataIndex: 'order',
        key: 'order',
        width: '5%',
        title: <TableHeaderCell titleKey="titleOrder" tooltipContent={t('order')} />,
        render: (order, { key, compressorId }) =>
          !editEnabled ||
          (fromRulesEditor && isNil(order)) ||
          !getFieldEditableStatusForAllCompressors(compressors, compressorId, 'order') ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {order ?? <span>&mdash;</span>}
            </span>
          ) : (
            <div>
              <Input
                value={order}
                placeholder="+"
                onChange={onDefaultSetpointFieldChange(key, 'order')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onOrderColumnKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                $hasBeenEdited={
                  order !==
                  (fromRulesGeneratorModal
                    ? find(rulesObject?.oldValues?.rules, { key })?.order ?? order
                    : engineStore[
                        fromRulesEditor ? SetpointsConfigurationConstant : EngineStepsEnum.DefaultSetpointsConfiguration
                      ]?.getOldSetpointByKey(key)?.order)
                }
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${order ?? ''}`?.length}
                $hasFinishedWizard={engineStore.hasFinishedWizard}
                className={
                  ((fromRulesGeneratorModal && isNewFallbackScenario) || hasTriedToSubmit) &&
                  !disabledCompressors?.includes(compressorId) &&
                  cn(find(validationMessages, { key, field: 'order' })?.message && 'validation-error-indicator')
                }
              />
            </div>
          ),
      },
      {
        dataIndex: 'load',
        key: 'load',
        width: '5%',
        title: <TableHeaderCell titleKey="titleLoad" tooltipContent={t('load')} />,
        render: (load, { key, compressorId }) =>
          !editEnabled ||
          (fromRulesEditor && isNil(load)) ||
          !getFieldEditableStatusForAllCompressors(compressors, compressorId, 'load') ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {load ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                hasTriedToSubmit &&
                (editingSetpointId ? editingSetpointId === `${key}-load` : true) &&
                find(engineStore.validationMessages, { key, field: 'load' })?.message
              }
              tArgs={find(engineStore.validationMessages, { key, field: 'load' })?.tArgs}
              placement="right"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 400 }}
            >
              <Input
                value={load}
                placeholder="+"
                onChange={onDefaultSetpointFieldChange(key, 'load')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultSetpointKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                $hasBeenEdited={
                  load !==
                  (fromRulesGeneratorModal
                    ? find(rulesObject?.oldValues?.rules, { key })?.load ?? load
                    : engineStore[
                        fromRulesEditor ? SetpointsConfigurationConstant : EngineStepsEnum.DefaultSetpointsConfiguration
                      ]?.getOldSetpointByKey(key)?.load)
                }
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${load ?? ''}`?.length}
                $hasFinishedWizard={engineStore.hasFinishedWizard}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'vsdTarget',
        key: 'vsdTarget',
        width: '5%',
        title: <TableHeaderCell titleKey="titleTarget" tooltipContent={t('Target')} />,
        render: (vsdTarget, { key, compressorId }) =>
          !editEnabled ||
          (fromRulesEditor && isNil(vsdTarget)) ||
          !getFieldEditableStatusForAllCompressors(compressors, compressorId, 'vsdTarget') ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {vsdTarget ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                hasTriedToSubmit &&
                (editingSetpointId ? editingSetpointId === `${key}-vsdTarget` : true) &&
                find(engineStore.validationMessages, { key, field: 'vsdTarget' })?.message
              }
              tArgs={find(engineStore.validationMessages, { key, field: 'vsdTarget' })?.tArgs}
              placement="right"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 400 }}
            >
              <Input
                value={vsdTarget}
                placeholder="+"
                onChange={onDefaultSetpointFieldChange(key, 'vsdTarget')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultSetpointKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                $hasBeenEdited={
                  vsdTarget !==
                  (fromRulesGeneratorModal
                    ? find(rulesObject?.oldValues?.rules, { key })?.vsdTarget ?? vsdTarget
                    : engineStore[
                        fromRulesEditor ? SetpointsConfigurationConstant : EngineStepsEnum.DefaultSetpointsConfiguration
                      ]?.getOldSetpointByKey(key)?.vsdTarget)
                }
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${vsdTarget ?? ''}`?.length}
                $hasFinishedWizard={engineStore.hasFinishedWizard}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'unload',
        key: 'unload',
        title: <TableHeaderCell titleKey="titleUnload" tooltipContent={t('Unload')} />,
        width: '5%',
        render: (unload, { key, compressorId }) =>
          !editEnabled ||
          (fromRulesEditor && isNil(unload)) ||
          !getFieldEditableStatusForAllCompressors(compressors, compressorId, 'unload') ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {unload ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                hasTriedToSubmit &&
                (editingSetpointId ? editingSetpointId === `${key}-unload` : true) &&
                find(engineStore.validationMessages, { key, field: 'unload' })?.message
              }
              tArgs={find(engineStore.validationMessages, { key, field: 'unload' })?.tArgs}
              placement="right"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 400 }}
            >
              <Input
                value={unload}
                placeholder="+"
                onChange={onDefaultSetpointFieldChange(key, 'unload')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultSetpointKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                $hasBeenEdited={
                  unload !==
                  (fromRulesGeneratorModal
                    ? find(rulesObject?.oldValues?.rules, { key })?.unload ?? unload
                    : engineStore[
                        fromRulesEditor ? SetpointsConfigurationConstant : EngineStepsEnum.DefaultSetpointsConfiguration
                      ]?.getOldSetpointByKey(key)?.unload)
                }
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${unload ?? ''}`?.length}
                $hasFinishedWizard={engineStore.hasFinishedWizard}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'autoStop',
        key: 'autoStop',
        width: '5%',
        title: <TableHeaderCell titleKey="titleAutoStop" tooltipContent={t('autoStop')} />,
        render: (autoStop, { key, compressorId }) =>
          !editEnabled ||
          (fromRulesEditor && isNil(autoStop)) ||
          !getFieldEditableStatusForAllCompressors(compressors, compressorId, 'autoStop') ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {autoStop ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                hasTriedToSubmit &&
                (editingSetpointId ? editingSetpointId === `${key}-autoStop` : true) &&
                find(engineStore.validationMessages, { key, field: 'autoStop' })?.message
              }
              tArgs={find(engineStore.validationMessages, { key, field: 'autoStop' })?.tArgs}
              placement="right"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 400 }}
            >
              <Input
                value={autoStop}
                placeholder="+"
                onChange={onDefaultSetpointFieldChange(key, 'autoStop')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultSetpointKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                $hasBeenEdited={
                  autoStop !==
                  (fromRulesGeneratorModal
                    ? find(rulesObject?.oldValues?.rules, { key })?.autoStop ?? autoStop
                    : engineStore[
                        fromRulesEditor ? SetpointsConfigurationConstant : EngineStepsEnum.DefaultSetpointsConfiguration
                      ]?.getOldSetpointByKey(key)?.autoStop)
                }
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${autoStop ?? ''}`?.length}
                $hasFinishedWizard={engineStore.hasFinishedWizard}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'immediateStop',
        key: 'immediateStop',
        width: '5%',
        title: <TableHeaderCell titleKey="titleImmediateStop" tooltipContent={t('immediateStop')} />,
        render: (immediateStop, { key, compressorId }) =>
          !editEnabled ||
          (fromRulesEditor && isNil(immediateStop)) ||
          !getFieldEditableStatusForAllCompressors(compressors, compressorId, 'immediateStop') ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {immediateStop ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                hasTriedToSubmit &&
                (editingSetpointId ? editingSetpointId === `${key}-immediateStop` : true) &&
                find(engineStore.validationMessages, { key, field: 'immediateStop' })?.message
              }
              tArgs={find(engineStore.validationMessages, { key, field: 'immediateStop' })?.tArgs}
              placement="right"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 400 }}
            >
              <Input
                value={immediateStop}
                placeholder="+"
                onChange={onDefaultSetpointFieldChange(key, 'immediateStop')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultSetpointKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                $hasBeenEdited={
                  immediateStop !==
                  (fromRulesGeneratorModal
                    ? find(rulesObject?.oldValues?.rules, { key })?.immediateStop ?? immediateStop
                    : engineStore[
                        fromRulesEditor ? SetpointsConfigurationConstant : EngineStepsEnum.DefaultSetpointsConfiguration
                      ]?.getOldSetpointByKey(key)?.immediateStop)
                }
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${immediateStop ?? ''}`?.length}
                $hasFinishedWizard={engineStore.hasFinishedWizard}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'pressureDrop',
        key: 'pressureDrop',
        width: '5%',
        title: <TableHeaderCell titleKey="titlePressureDrop" tooltipContent={t('pressureDrop')} />,
        render: (pressureDrop, { key, compressorId }) =>
          !editEnabled ||
          (fromRulesEditor && isNil(pressureDrop)) ||
          !getFieldEditableStatusForAllCompressors(compressors, compressorId, 'pressureDrop') ? (
            <span
              className={cn(
                isEditMode && 'margin-left-2',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              {pressureDrop ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                hasTriedToSubmit &&
                (editingSetpointId ? editingSetpointId === `${key}-pressureDrop` : true) &&
                find(engineStore.validationMessages, { key, field: 'pressureDrop' })?.message
              }
              tArgs={find(engineStore.validationMessages, { key, field: 'pressureDrop' })?.tArgs}
              placement="right"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 400 }}
            >
              <Input
                value={pressureDrop}
                placeholder="+"
                onChange={onDefaultSetpointFieldChange(key, 'pressureDrop')}
                onWheel={onInputScroll}
                bordered={false}
                count={inputCountArgs}
                onKeyPress={onDefaultSetpointKeyPress}
                onBlur={onInputBlur}
                $fromRulesEditor={fromRulesEditor}
                $hasBeenEdited={
                  pressureDrop !==
                  (fromRulesGeneratorModal
                    ? find(rulesObject?.oldValues?.rules, { key })?.pressureDrop ?? pressureDrop
                    : engineStore[
                        fromRulesEditor ? SetpointsConfigurationConstant : EngineStepsEnum.DefaultSetpointsConfiguration
                      ]?.getOldSetpointByKey(key)?.pressureDrop)
                }
                disabled={disabledCompressors?.includes(compressorId)}
                $valueLength={`${pressureDrop ?? ''}`?.length}
                $hasFinishedWizard={engineStore.hasFinishedWizard}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'modulate',
        key: 'modulate',
        width: '5%',
        title: (
          <TableHeaderCell
            titleKey="titleModulate"
            className={cn(fromRulesEditor && 'modulate-header')}
            tooltipContent={t('modulate')}
          />
        ),
        render: (modulate, { key, compressorId }) =>
          (fromRulesEditor && !modulate && !editEnabled) ||
          !getFieldEditableStatusForAllCompressors(compressors, compressorId, 'modulate') ? (
            <span
              className={cn(
                'no-value',
                'margin-left-4',
                fromRulesEditor && 'view-mode-table-cell',
                disabledCompressors?.includes(compressorId) && 'disabled-compressor',
              )}
            >
              &mdash;
            </span>
          ) : (
            <CheckboxContainer
              $hasBeenEdited={
                modulate !==
                (fromRulesGeneratorModal
                  ? find(rulesObject?.oldValues?.rules, { key })?.modulate ?? modulate
                  : engineStore[
                      fromRulesEditor ? SetpointsConfigurationConstant : EngineStepsEnum.DefaultSetpointsConfiguration
                    ]?.getOldSetpointByKey(key)?.modulate)
              }
              $fromRulesEditor={fromRulesEditor}
              $disabledCompressor={disabledCompressors?.includes(compressorId)}
              $hasFinishedWizard={engineStore.hasFinishedWizard}
            >
              <EPCheckbox
                checked={modulate}
                onChange={onDefaultSetpointFieldChange(key, 'modulate')}
                className={cn('modulate-checkbox', !editEnabled && 'view-mode-modulate-checkbox')}
                disabled={!editEnabled || disabledCompressors?.includes(compressorId)}
              />
            </CheckboxContainer>
          ),
      },
      {
        dataIndex: 'bandConfiguration',
        key: 'bandConfiguration',
        width: '5%',
        title: <TableHeaderCell titleKey="titleBandConfiguration" tooltipContent={t('bandConfiguration')} />,
        render: (bandConfiguration, { key, compressorId }) => {
          const tooltipContent = compressorTooltipContents[compressorId] || <>{t('noMetricsAvailable')}</>;

          const renderSelectOptions = () => {
            return bandConfigurationOptions.map(option => (
              <EPSelectOption value={option} key={option}>
                {option !== LONG_DASH ? (
                  <Tooltip title={tooltipContent} placement="right">
                    <span>{option}</span>
                  </Tooltip>
                ) : (
                  <span>{option}</span>
                )}
              </EPSelectOption>
            ));
          };

          return !isEditMode ||
            !editEnabled ||
            (fromRulesEditor && isNil(bandConfiguration)) ||
            !getFieldEditableStatusForAllCompressors(compressors, compressorId, 'bandConfiguration') ? (
            bandConfiguration && bandConfiguration !== LONG_DASH ? (
              <Tooltip title={tooltipContent} placement="right" open={canOpenCompressorMetricTooltip}>
                <span
                  className={cn(
                    isEditMode && 'margin-left-2',
                    fromRulesEditor && 'view-mode-table-cell',
                    disabledCompressors?.includes(compressorId) && 'disabled-compressor',
                  )}
                >
                  {bandConfiguration ?? <span>&mdash;</span>}
                </span>
              </Tooltip>
            ) : (
              <span
                className={cn(
                  isEditMode && 'margin-left-2',
                  fromRulesEditor && 'view-mode-table-cell',
                  disabledCompressors?.includes(compressorId) && 'disabled-compressor',
                )}
              >
                {bandConfiguration ?? <span>&mdash;</span>}
              </span>
            )
          ) : (
            <div style={{ width: '100%', pointerEvents: 'auto' }}>
              {bandConfiguration && bandConfiguration !== LONG_DASH ? (
                <EPSelect
                  value={bandConfiguration ?? LONG_DASH}
                  onChange={onDefaultSetpointFieldChange(key, 'bandConfiguration')}
                  disabled={disabledCompressors?.includes(compressorId) || engineStore.isReadOnly || !editEnabled}
                  size="small"
                  onDropdownVisibleChange={setIsDropdownOpen}
                >
                  {renderSelectOptions()}
                </EPSelect>
              ) : (
                <EPSelect
                  value={bandConfiguration ?? LONG_DASH}
                  onChange={onDefaultSetpointFieldChange(key, 'bandConfiguration')}
                  disabled={disabledCompressors?.includes(compressorId) || engineStore.isReadOnly || !editEnabled}
                  size="small"
                  onDropdownVisibleChange={setIsDropdownOpen}
                >
                  {renderSelectOptions()}
                </EPSelect>
              )}
            </div>
          );
        },
      },
    ];

    const allSetpoints: Setpoint[] =
      sortSetpointsByElementIndex(setpoints, devicesStore?.sortedCompressors) ??
      engineStore.defaultSetpointsConfiguration.getSetpointsByPressureRange();

    const filteredSetpoints = (() => {
      if (!allSetpoints || engineStore.isReadOnly) {
        return allSetpoints;
      }

      const filteredCompressorIds = new Set(engineStore.applicableCompressorsForEngine.map(c => c.id));
      return allSetpoints.filter(setpoint => filteredCompressorIds.has(setpoint.compressorId));
    })();

    return compressors?.length ? (
      <DefaultRuleContainer
        className={className}
        $fromRulesEditor={fromRulesEditor}
        $hasFinishedWizard={engineStore.hasFinishedWizard}
        $isReadOnly={engineStore.isReadOnly}
      >
        <EPTable
          columns={columns}
          dataSource={filteredSetpoints}
          pagination={false}
          className="table"
          {...(filteredSetpoints?.length > (engineStore.hasFinishedWizard ? 6 : 4) &&
            !fromRulesEditor && {
              scroll: { y: engineStore.hasFinishedWizard ? 405 : 300 },
            })}
        />
        <span className="info-message">{t('defaultSetpointsInfoMessage')}</span>
      </DefaultRuleContainer>
    ) : null;
  },
);

export default DefaultSetpointsComponent;
